import React, { useState } from "react";
import axios from "axios";
import { IoCloseOutline, IoChevronDownOutline } from "react-icons/io5";
import { REGIONS_DATA } from "../utils/regionData";
import { COUNTRY_CODES } from "../utils/countryCodes";

const InquiryModal = ({ show, onClose, report, type }) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    businessEmail: "",
    companyName: "",
    countryCode: "",
    phoneNumber: "",
    region: "Global",
    country: "",
    message: "",
    budgetRange: "",
    timeline: "",
    purpose: "",
  });

  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const countryDropdownRef = React.useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(event.target)
      ) {
        setIsCountryDropdownOpen(false);
        setCountrySearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const [countries] = useState(COUNTRY_CODES);
  const [countrySearch, setCountrySearch] = useState("");

  const filteredCountries = countries.filter((c) => {
    if (!countrySearch.trim()) return true;
    const q = countrySearch.trim().toLowerCase();
    const cleanCode = c.code.replace("+", "");
    const cleanQ = q.replace("+", "");
    return (
      c.name.toLowerCase().includes(q) ||
      c.code.toLowerCase().includes(q) ||
      cleanCode.startsWith(cleanQ)
    );
  });

  const [selectedRegion, setSelectedRegion] = useState(REGIONS_DATA[0]);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  React.useEffect(() => {
    if (show) {
      document.body.style.overflow = "hidden";
      try {
        const storedUser = JSON.parse(
          localStorage.getItem("rw_end_user") || "{}",
        );
        if (storedUser?.email) {
          const names = (storedUser.name || "").trim().split(" ");
          setFormData((prev) => ({
            ...prev,
            businessEmail: prev.businessEmail || storedUser.email || "",
            firstName:
              prev.firstName || storedUser.firstName || names[0] || "",
            lastName:
              prev.lastName ||
              storedUser.lastName ||
              names.slice(1).join(" ") ||
              "",
            companyName:
              prev.companyName ||
              storedUser.companyName ||
              storedUser.company ||
              "",
            phoneNumber: prev.phoneNumber || storedUser.phone || "",
          }));
        }
      } catch (e) {}
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [show]);

  const handleRegionChange = (e) => {
    const region = REGIONS_DATA.find((r) => r.name === e.target.value);
    if (region) {
      setSelectedRegion(region);
      setFormData({
        ...formData,
        region: region.name,
        country: "",
      });
    }
  };

  const handleCountryChange = (e) => {
    setFormData({ ...formData, country: e.target.value });
  };

  if (!show) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (type === "sample_download" && !agreedToTerms) {
      setError("Please agree to the terms and privacy policy.");
      return;
    }

    if (!formData.countryCode || formData.countryCode === "Select") {
      setError("Please select a country code.");
      return;
    }

    if (!formData.country || formData.country === "All Countries") {
      setError("Please select a country.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/inquiries`,
        {
          ...formData,
          phoneNumber: `${formData.countryCode} ${formData.phoneNumber}`,
          fullName: `${formData.firstName} ${formData.lastName}`,
          reportId: report.id,
          type: type,
        },
      );

      if (response.data.success) {
        setSuccess(true);
        try {
          const userEmail = (formData.businessEmail || "").toLowerCase().trim();
          const recentKey = "rw_recent_inquiries";
          const existing = JSON.parse(localStorage.getItem(recentKey) || "[]");
          const normalizedType = (type === "sample" || type === "sample_download") ? "sample" : "customization";
          const newEntry = {
            reportId: report?.id,
            reportSlug: report?.slug,
            reportTitle: report?.title,
            email: userEmail,
            type: normalizedType,
            submittedAt: new Date().toISOString(),
            timestamp: Date.now(),
          };
          const updated = [
            newEntry,
            ...existing.filter(
              (item) => !(item.reportId === report?.id && item.email === userEmail && item.type === normalizedType)
            ),
          ].slice(0, 100);
          localStorage.setItem(recentKey, JSON.stringify(updated));
          window.dispatchEvent(new Event("rwire_inquiry_submitted"));
        } catch (e) {
          console.warn("Failed to cache recent inquiry", e);
        }
        window.dispatchEvent(new Event("rwire_customized_reports_updated"));
      }
    } catch (err) {
      setError(
        err.response?.data?.error || "Something went wrong. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleOnClose = () => {
    setSuccess(false);
    setFormData({
      firstName: "",
      lastName: "",
      businessEmail: "",
      companyName: "",
      countryCode: "",
      phoneNumber: "",
      region: "Global",
      country: "",
      message: "",
      budgetRange: "",
      timeline: "",
      purpose: "",
    });
    setSelectedRegion(REGIONS_DATA[0]);
    setAgreedToTerms(false);
    onClose();
  };

  const buttonText =
    type === "sample_download"
      ? "Request a Sample"
      : type === "schedule_call"
        ? "Request a Call"
        : "Submit Request";

  return (
    <div
      className="modal show d-block"
      style={{
        backgroundColor: "rgba(0,0,0,0.6)",
        zIndex: 1060,
        padding: "20px",
      }}
    >
      <div
        className={`modal-dialog modal-dialog-centered ${success ? "modal-md" : "modal-md"}`}
        style={{ maxWidth: success ? "500px" : "650px" }}
      >
        <div
          className="modal-content border-0 p-3 p-md-4 py-2"
          style={{ borderRadius: "16px", overflow: "hidden" }}
        >
          <div className="d-flex justify-content-between align-items-center mb-2 mt-2">
            <h5 className="fw-bold m-0" style={{ fontSize: "1.2rem" }}>
              {type === "sample_download"
                ? "Request a Sample"
                : type === "schedule_call"
                  ? "Schedule a Call"
                  : "Report Customization"}
            </h5>
            <button
              className="btn p-0"
              onClick={handleOnClose}
              disabled={loading}
            >
              <IoCloseOutline size={24} />
            </button>
          </div>

          {success ? (
            <div className="text-center py-5 px-4">
              <div className="mb-4" style={{ color: "#2ecc71" }}>
                <svg
                  width="84"
                  height="84"
                  viewBox="0 0 100 100"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="50" cy="50" r="48" fill="#e8f8ef" />
                  <path
                    d="M35 50L45 60L65 40"
                    stroke="#2ecc71"
                    strokeWidth="6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3 className="fw-bold mb-3" style={{ fontSize: "1.5rem" }}>
                {type === "sample_download"
                  ? "Request Submitted!"
                  : type === "schedule_call"
                    ? "Meeting Requested!"
                    : "Request Submitted!"}
              </h3>
              <p
                className="text-secondary mb-5"
                style={{ fontSize: "0.95rem", lineHeight: "1.6" }}
              >
                {type === "sample_download"
                  ? "You will receive the requested sample on the registered email address within 1-2 business working days."
                  : type === "schedule_call"
                    ? "Our team will contact you to schedule a call within the next 24 hours."
                    : "Our research team will review your requirements and reach out to you within 2-3 working days."}
              </p>
              <button
                className="btn px-5 py-2 fw-bold text-white w-100"
                style={{
                  backgroundColor: "#053b84",
                  borderRadius: "8px",
                  border: "none",
                  height: "48px",
                }}
                onClick={handleOnClose}
              >
                Done
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {/* Report Title Banner */}
              <div
                className="mb-2 p-2 px-3"
                style={{
                  backgroundColor: "#f8faff",
                  borderRadius: "6px",
                  borderLeft: "3px solid #053b84",
                }}
              >
                <p
                  className="small fw-bold text-primary mb-0"
                  style={{ fontSize: "0.8rem" }}
                >
                  {report.title}
                </p>
              </div>

              <div className="row g-2">
                <div className="col-6 col-md-6">
                  <label className="form-label x-small fw-bold mb-1">
                    First Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    className="form-control bg-light border-0 py-1 shadow-none"
                    style={{ height: "38px", fontSize: "0.85rem" }}
                    placeholder="First Name"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="col-6 col-md-6">
                  <label className="form-label x-small fw-bold mb-1">
                    Last Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    className="form-control bg-light border-0 py-1 shadow-none"
                    style={{ height: "38px", fontSize: "0.85rem" }}
                    placeholder="Last Name"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label x-small fw-bold mb-1">
                    Company Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="companyName"
                    className="form-control bg-light border-0 py-1 shadow-none"
                    style={{ height: "38px", fontSize: "0.85rem" }}
                    placeholder="Company"
                    value={formData.companyName}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label x-small fw-bold mb-1">
                    Work Email <span className="text-danger">*</span>
                  </label>
                  <input
                    type="email"
                    name="businessEmail"
                    className="form-control bg-light border-0 py-1 shadow-none"
                    style={{ height: "38px", fontSize: "0.85rem" }}
                    placeholder="john@email.com"
                    value={formData.businessEmail}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="col-12 col-md-5">
                  <label className="form-label x-small fw-bold mb-1">
                    Phone <span className="text-danger">*</span>
                  </label>
                  <div className="d-flex gap-1">
                    <div
                      className="country-code-selector-container"
                      ref={countryDropdownRef}
                      style={{ position: "relative" }}
                    >
                      <div
                        className="d-flex align-items-center justify-content-between bg-light rounded px-2 cursor-pointer"
                        style={{
                          height: "38px",
                          fontSize: "0.8rem",
                          width: "90px",
                          border: "none",
                          cursor: "pointer",
                        }}
                        onClick={() =>
                          setIsCountryDropdownOpen(!isCountryDropdownOpen)
                        }
                      >
                        <span>{formData.countryCode || "Select"}</span>
                        <IoChevronDownOutline
                          size={12}
                          className={`text-secondary ${isCountryDropdownOpen ? "rotate-180" : ""}`}
                          style={{ transition: "transform 0.3s" }}
                        />
                      </div>
                      {isCountryDropdownOpen && (
                        <div
                          className="country-dropdown-list shadow-lg"
                          style={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            width: "90px",
                            maxHeight: "220px",
                            overflowY: "auto",
                            backgroundColor: "white",
                            border: "1px solid #eee",
                            borderRadius: "8px",
                            zIndex: 2000,
                            marginTop: "5px",
                          }}
                        >
                          <div
                            style={{
                              position: "sticky",
                              top: 0,
                              backgroundColor: "white",
                              padding: "4px",
                              borderBottom: "1px solid #eee",
                              zIndex: 10,
                            }}
                          >
                            <input
                              type="text"
                              placeholder="Search..."
                              value={countrySearch}
                              onChange={(e) => setCountrySearch(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  if (filteredCountries.length > 0) {
                                    setFormData({
                                      ...formData,
                                      countryCode: filteredCountries[0].code,
                                    });
                                    setIsCountryDropdownOpen(false);
                                    setCountrySearch("");
                                  }
                                }
                              }}
                              autoFocus
                              style={{
                                width: "100%",
                                fontSize: "0.72rem",
                                padding: "2px 4px",
                                border: "1px solid #cbd5e1",
                                borderRadius: "4px",
                                outline: "none",
                              }}
                            />
                          </div>
                          {filteredCountries.length === 0 ? (
                            <div
                              className="text-center text-muted py-2"
                              style={{ fontSize: "0.72rem" }}
                            >
                              No match
                            </div>
                          ) : (
                            filteredCountries.map((c, i) => (
                              <div
                                key={i}
                                className="d-flex align-items-center justify-content-start gap-1 px-2 py-1 hover-bg-light cursor-pointer"
                                style={{
                                  fontSize: "0.8rem",
                                  cursor: "pointer",
                                  transition: "background 0.2s",
                                }}
                                onClick={() => {
                                  setFormData({
                                    ...formData,
                                    countryCode: c.code,
                                  });
                                  setIsCountryDropdownOpen(false);
                                  setCountrySearch("");
                                }}
                                onMouseEnter={(e) =>
                                  (e.currentTarget.style.backgroundColor =
                                    "#f8f9fa")
                                }
                                onMouseLeave={(e) =>
                                  (e.currentTarget.style.backgroundColor =
                                    "transparent")
                                }
                              >
                                <span style={{ fontSize: "0.85rem" }}>
                                  {c.flag}
                                </span>
                                <span className="fw-bold">{c.code}</span>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                    <input
                      type="tel"
                      name="phoneNumber"
                      className="form-control bg-light border-0 py-1 shadow-none flex-grow-1"
                      style={{ height: "38px", fontSize: "0.85rem" }}
                      placeholder=" "
                      value={formData.phoneNumber}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <label className="form-label x-small fw-bold mb-1">
                    Region <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select bg-light border-0 py-1 shadow-none"
                    style={{ height: "38px", fontSize: "0.85rem" }}
                    value={formData.region}
                    onChange={handleRegionChange}
                    required
                  >
                    {REGIONS_DATA.map((r) => (
                      <option key={r.id} value={r.name}>
                        {r.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-6 col-md-4">
                  <label className="form-label x-small fw-bold mb-1">
                    Country <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select bg-light border-0 py-1 shadow-none"
                    style={{ height: "38px", fontSize: "0.85rem" }}
                    value={formData.country}
                    onChange={handleCountryChange}
                    required
                  >
                    <option value="">Choose Country</option>
                    {selectedRegion.countries.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-12">
                  <label className="form-label x-small fw-bold mb-1">
                    {type === "sample_download"
                      ? "Purpose"
                      : "Custom Requirements"}
                  </label>
                  <textarea
                    name="message"
                    className="form-control bg-light border-0 py-2 shadow-none"
                    rows="2"
                    style={{ fontSize: "0.85rem" }}
                    placeholder={
                      type === "sample_download"
                        ? "Plan to use this report..."
                        : "Describe your specific customizations..."
                    }
                    value={formData.message}
                    onChange={handleChange}
                  ></textarea>
                </div>

                {type === "sample_download" && (
                  <div className="col-12">
                    <div className="form-check small mt-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="agreedToTerms"
                        checked={agreedToTerms}
                        onChange={(e) => setAgreedToTerms(e.target.checked)}
                        required
                      />
                      <label
                        className="form-check-label text-muted"
                        htmlFor="agreedToTerms"
                      >
                        I agree to the terms & conditions and privacy policy
                      </label>
                    </div>
                  </div>
                )}
              </div>

              {error && (
                <div className="alert alert-danger mt-2 py-2 small">
                  {error}
                </div>
              )}

              <div className="d-flex gap-2 justify-content-end mt-2 pt-2 pb-2">
                <button
                  type="button"
                  className="btn btn-light px-4 py-1 small fw-bold"
                  onClick={handleOnClose}
                  disabled={loading}
                  style={{ borderRadius: "8px", fontSize: "0.85rem" }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn px-4 py-1 small fw-bold text-white shadow-sm"
                  style={{
                    background: "linear-gradient(0deg, #012970, #024ed6)",
                    borderRadius: "8px",
                    border: "none",
                    minWidth: "150px",
                    fontSize: "0.85rem",
                    height: "38px",
                  }}
                  disabled={loading}
                >
                  {loading ? (
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                  ) : type === "sample_download" ? (
                    <i className="bi bi-file-earmark-text me-2"></i>
                  ) : (
                    <i className="bi bi-send me-2"></i>
                  )}
                  {buttonText}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default InquiryModal;
